import type { SelectChangeEvent } from '@mui/material';
import {
  Checkbox,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  inputLabelClasses,
} from '@mui/material';
import React from 'react';
import { SearchInput } from '../../../../../components';
import { TranslationKeys } from '../../../../../translations';
import { useIntl } from 'react-intl';
import { DropdownStyled } from '../../../../Connections/Components/EventsToolbar/EventsToolbar.styles';
import { ComparisonItemType } from '../../../../../types/BaselineMeasurements';

type ComparisonToolbarProps = {
  isLoading?: boolean;
  selectedItemTypes: string[];
  onFilterItemTypes: (filter: string[]) => void;
  archiveEvents: () => void;
  archiveAllEvents: () => void;
  processEvents: () => void;
  processAllEvents: () => void;
  declineEvents: () => void;
  declineAllEvents: () => void;
  downloadSelected: () => void;
  downloadAll: () => void;
  hasSelectedItems: boolean;
  onSearch: (inputValue: string) => void;
  searchQuery?: string;
};

export const ComparisonToolBar = ({
  isLoading,
  selectedItemTypes,
  onFilterItemTypes,
  archiveEvents,
  archiveAllEvents,
  processEvents,
  processAllEvents,
  declineEvents,
  declineAllEvents,
  downloadSelected,
  downloadAll,
  hasSelectedItems,
  onSearch,
  searchQuery,
}: ComparisonToolbarProps) => {
  const intl = useIntl();

  const onChangeItemTypeFilter = React.useCallback(
    (event: SelectChangeEvent<string[]>) => {
      const value = event.target.value;
      onFilterItemTypes(typeof value === 'string' ? value.split(',') : value);
    },
    [onFilterItemTypes],
  );

  const onChangeSearchInput = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onSearch(event.target.value);
    },
    [onSearch],
  );

  const dropdownOptions = {
    archive: [
      {
        title: intl.formatMessage({ id: TranslationKeys['baseline_comparison_actions_events_archive-selected'] }),
        tooltip: 'Archive selected items',
        confirmDialog: {
          title: intl.formatMessage({
            id: TranslationKeys['baseline_comparison_actions_events_archive-selected_confirm_title'],
          }),
          message: intl.formatMessage({
            id: TranslationKeys['baseline_comparison_actions_events_archive-selected_confirm_message'],
          }),
        },
        action: archiveEvents,
        disabled: isLoading || false,
      },
      {
        title: intl.formatMessage({ id: TranslationKeys['baseline_comparison_actions_events_archive-all'] }),
        tooltip: 'Archive all items from comparison',
        confirmDialog: {
          title: intl.formatMessage({
            id: TranslationKeys['baseline_comparison_actions_events_archive-all_confirm_title'],
          }),
          message: intl.formatMessage({
            id: TranslationKeys['baseline_comparison_actions_events_archive-all_confirm_message'],
          }),
        },
        action: archiveAllEvents,
        disabled: isLoading || false,
      },
    ],
    process: [
      {
        title: intl.formatMessage({ id: TranslationKeys['baseline_comparison_actions_events_process-selected'] }),
        tooltip: 'Process selected items from comparison',
        confirmDialog: {
          title: intl.formatMessage({
            id: TranslationKeys['baseline_comparison_actions_events_process-selected_confirm_title'],
          }),
          message: intl.formatMessage({
            id: TranslationKeys['baseline_comparison_actions_events_process-selected_confirm_message'],
          }),
        },
        action: processEvents,
        disabled: isLoading || false,
      },
      {
        title: intl.formatMessage({ id: TranslationKeys['baseline_comparison_actions_events_process-all'] }),
        tooltip: 'Process all items from comparison',
        confirmDialog: {
          title: intl.formatMessage({
            id: TranslationKeys['baseline_comparison_actions_events_process-all_confirm_title'],
          }),
          message: intl.formatMessage({
            id: TranslationKeys['baseline_comparison_actions_events_process-all_confirm_message'],
          }),
        },
        action: processAllEvents,
        disabled: isLoading || false,
      },
    ],
    decline: [
      {
        title: intl.formatMessage({ id: TranslationKeys['baseline_comparison_actions_events_decline-selected'] }),
        tooltip: 'Removeeeeee selected items from comparison',
        confirmDialog: {
          title: intl.formatMessage({
            id: TranslationKeys['baseline_comparison_actions_events_decline-selected_confirm_title'],
          }),
          message: intl.formatMessage({
            id: TranslationKeys['baseline_comparison_actions_events_decline-selected_confirm_message'],
          }),
        },
        action: declineEvents,
        disabled: isLoading || false,
      },
      {
        title: intl.formatMessage({ id: TranslationKeys['baseline_comparison_actions_events_decline-all'] }),
        tooltip: 'Removedafaf all items from comparison',
        confirmDialog: {
          title: intl.formatMessage({
            id: TranslationKeys['baseline_comparison_actions_events_decline-all_confirm_title'],
          }),
          message: intl.formatMessage({
            id: TranslationKeys['baseline_comparison_actions_events_decline-all_confirm_message'],
          }),
        },
        action: declineAllEvents,
        disabled: isLoading || false,
      },
    ],
    download: [
      {
        title: intl.formatMessage({ id: TranslationKeys['baseline_comparison_actions_events_download-selected'] }),
        tooltip: intl.formatMessage({
          id: TranslationKeys['baseline_comparison_actions_events_download-selected'],
        }),
        confirmDialog: {
          title: intl.formatMessage({
            id: TranslationKeys['baseline_comparison_actions_events_download-selected_confirm_title'],
          }),
          message: intl.formatMessage({
            id: TranslationKeys['baseline_comparison_actions_events_download-selected_confirm_message'],
          }),
        },
        action: downloadSelected,
        disabled: isLoading || !hasSelectedItems,
      },
      {
        title: intl.formatMessage({ id: TranslationKeys['baseline_comparison_actions_events_download-all'] }),
        tooltip: intl.formatMessage({
          id: TranslationKeys['baseline_comparison_actions_events_download-all'],
        }),
        confirmDialog: {
          title: intl.formatMessage({
            id: TranslationKeys['baseline_comparison_actions_events_download-all_confirm_title'],
          }),
          message: intl.formatMessage({
            id: TranslationKeys['baseline_comparison_actions_events_download-all_confirm_message'],
          }),
        },
        action: downloadAll,
        disabled: isLoading || false,
      },
    ],
  };

  const itemTypeOptions: Array<{ value: string; label: string }> = React.useMemo(() => {
    return Object.values(ComparisonItemType).map(value => ({
      value,
      label: intl.formatMessage({ id: `baseline.comparison.itemType.${value.toLowerCase()}` }),
    }));
  }, [intl]);

  return (
    <Paper sx={{ p: 3, mb: 1, borderRadius: 2 }}>
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={12} md={3}>
          <FormControl
            fullWidth
            disabled={isLoading}
            variant={'outlined'}
            sx={{
              minWidth: '150px',
              marginRight: '5px',
              height: '34px',
              [`& .${inputLabelClasses.outlined}`]: {
                [`&:not(.${inputLabelClasses.shrink})`]: {
                  transform: 'translate(14px, 5px) scale(1)',
                },
              },
              // Ajustes específicos para el Select sin size="small"
              '& .MuiOutlinedInput-root': {
                height: '34px',
                minHeight: '34px',
              },
              '& .MuiSelect-select': {
                padding: '8px 14px', // Padding similar al estándar sin size="small"
                display: 'flex',
                alignItems: 'center',
              },
            }}
          >
            <InputLabel id="comparison-item-type-filter-label">
              {intl.formatMessage({ id: TranslationKeys.baseline_comparison_filter_itemType_label })}
            </InputLabel>
            <Select
              data-testid={'comparison-item-type-filter'}
              labelId="comparison-item-type-filter-label"
              multiple
              value={selectedItemTypes}
              onChange={onChangeItemTypeFilter}
              input={
                <OutlinedInput
                  label={intl.formatMessage({ id: TranslationKeys.baseline_comparison_filter_itemType_label })}
                />
              }
              renderValue={selected => (
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {selected.map(value => (
                    <span
                      key={value}
                      style={{
                        padding: '2px 8px',
                        backgroundColor: '#dbeafe',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        color: '#1e40af',
                      }}
                    >
                      {intl.formatMessage({ id: `baseline.comparison.itemType.${value.toLowerCase()}` })}
                    </span>
                  ))}
                </Stack>
              )}
            >
              {itemTypeOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox color="secondary" checked={selectedItemTypes.includes(option.value)} />
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={2}>
          <SearchInput
            disabled={isLoading}
            onChange={onChangeSearchInput}
            initialSearchQuery={searchQuery}
            debounceTime={1000}
          />
        </Grid>

        <Grid item xs={12} md={7} justifyContent={'right'} alignItems={'right'} display={'flex'}>
          <Stack direction="row" spacing={1} sx={{ position: 'relative', zIndex: 1000 }}>
            <DropdownStyled
              data-testid={'comparison-download-item'}
              options={dropdownOptions.download}
              defaultSelectedIndex={1}
              intl
            />
            <DropdownStyled data-testid={'comparison-archive-item'} options={dropdownOptions.archive} intl />
            <DropdownStyled data-testid={'comparison-process-item'} options={dropdownOptions.process} intl />
            <DropdownStyled data-testid={'comparison-decline-item'} options={dropdownOptions.decline} intl />
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
};
